import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import { SERVICES } from "@/data/services";

const STATS = [
  ["13", "Service categories"],
  ["40+", "Community camps a year"],
  ["100%", "Published, honest fees"],
  ["7 days", "Support every week"],
];

const VALUES = [
  {
    title: "Access for everyone",
    text: "Services designed for citizens who are new to digital processes — plain language, assisted forms, and walk-in support.",
  },
  {
    title: "Transparency first",
    text: "Eligibility, required documents and fees are published upfront for every service. No hidden charges, ever.",
  },
  {
    title: "Local and accountable",
    text: "Our operators and advisors work in the communities they serve, and every booking is trackable online.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Kicker>About Us</Kicker>
          <h1 className="mt-4 font-display font-bold tracking-tight text-slate-900">
            <MaskedLine delay={0.1} className="text-4xl sm:text-5xl">
              NUC Service —
            </MaskedLine>
            <MaskedLine delay={0.22} className="text-2xl text-blue-900 sm:text-3xl lg:text-4xl">
              National Union Corporation
            </MaskedLine>
          </h1>
          <Reveal delay={0.35} className="mt-6 max-w-3xl">
            <p className="text-base leading-relaxed text-slate-700 sm:text-lg">
              We are a community service organisation helping citizens access digital services,
              government schemes, agricultural guidance, skills training, employment support and
              welfare programs — with dignity, transparency and speed.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="border-b border-slate-200">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <Kicker>Our Mission</Kicker>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              No citizen left behind by paperwork or technology
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="space-y-5 lg:col-span-7">
            <p className="text-base leading-relaxed text-slate-700">
              National Union Corporation began with a simple observation: the benefits exist —
              schemes, subsidies, training, certificates — but the path to them is confusing. We
              built NUC Service to be that missing bridge: a single trustworthy platform where any
              citizen can understand what they are eligible for, gather the right documents, book
              assistance and track the outcome.
            </p>
            <p className="text-base leading-relaxed text-slate-700">
              Today we run {SERVICES.length} service categories spanning agriculture, digital
              literacy, business guidance, health awareness and institutional collaboration with
              government bodies, NGOs and CSR programs.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>What We Stand For</Kicker>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="h-full rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
                  <span className="font-mono text-sm font-semibold text-blue-900">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-slate-900">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden px-4 py-16 sm:px-6 md:grid-cols-4 lg:px-8">
          {STATS.map(([value, label], i) => (
            <Reveal key={label} delay={i * 0.05}>
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <p className="font-display text-3xl font-bold tracking-tight text-blue-900 sm:text-4xl">
                  {value}
                </p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Ready to get started?
            </h2>
            <Link
              to="/services"
              data-testid="about-explore-button"
              className="inline-flex items-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
            >
              Explore our services
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
